import React from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import { X, Sparkles, Check } from "lucide-react";
import { Button } from "../ui/button";

export default function LanguagePicker() {
  const { showPicker, chooseLanguage, closePicker, lang } = useLanguage();
  const [pending, setPending] = React.useState(lang || "en");

  React.useEffect(() => { setPending(lang || "en"); }, [lang, showPicker]);

  if (!showPicker) return null;

  const options = [
    { code: "en", label: "English", sub: "United States", flag: "US" },
    { code: "fr", label: "Français", sub: "Canada", flag: "CA" },
  ];

  const title = pending === "fr" ? "Choisissez votre langue" : "Choose Your Language";
  const subtitle = pending === "fr"
    ? "Sélectionnez votre langue préférée. Vous pouvez la modifier à tout moment depuis le menu en haut à droite."
    : "Select your preferred language. You can switch anytime from the top-right menu.";
  const cont = pending === "fr" ? "Continuer" : "Continue";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-[#020408]/85 backdrop-blur-md" onClick={closePicker} />
      <div className="relative w-full max-w-lg glass rounded-2xl p-8 animate-in fade-in zoom-in duration-300">
        <button onClick={closePicker} aria-label="close" className="absolute top-4 right-4 w-9 h-9 rounded-lg border border-white/10 hover:border-cyan-400/40 text-slate-300 hover:text-white flex items-center justify-center transition">
          <X className="w-4 h-4" />
        </button>

        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/25 bg-cyan-500/10 mb-5">
          <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
          <span className="text-[10px] tracking-[0.22em] uppercase text-cyan-200 font-semibold">FortressPoint</span>
        </div>
        <h2 className="font-display text-2xl lg:text-3xl font-bold text-white">{title}</h2>
        <p className="mt-2 text-sm text-slate-400 leading-relaxed">{subtitle}</p>

        <div className="mt-6 grid grid-cols-2 gap-3">
          {options.map((o) => {
            const active = pending === o.code;
            return (
              <button
                key={o.code}
                onClick={() => setPending(o.code)}
                className={`relative rounded-xl p-4 text-left transition border ${
                  active
                    ? "border-cyan-400/50 bg-gradient-to-br from-sky-500/15 to-cyan-400/10 shadow-lg shadow-cyan-500/10"
                    : "border-white/10 bg-white/[0.03] hover:border-cyan-400/30"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display text-lg font-semibold text-white">{o.label}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{o.sub}</div>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-widest ${active ? "bg-cyan-500/25 text-cyan-100 border border-cyan-400/40" : "bg-white/5 text-slate-400 border border-white/10"}`}>
                    {o.flag}
                  </div>
                </div>
                {active && (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-cyan-300">
                    <Check className="w-3 h-3" strokeWidth={3} /> Selected
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <Button onClick={() => chooseLanguage(pending)} className="mt-6 w-full h-12 btn-primary rounded-xl font-semibold border-0">
          {cont}
        </Button>
      </div>
    </div>
  );
}
